"use client";

import { clsx } from "clsx";
import { debounce } from "lodash";
import { useRef, useCallback, useMemo, useEffect, useState } from "react";
import React from "react";
import { BrowseSearchV2 } from "@repo/apis/browse-search-v2";
import { getSuggestions, Suggestion } from "@repo/apis/browse-search-v2";
import { getCategoryProducts } from "@repo/apis/browse-search-v2";
import { useRouter, Link } from "@repo/next-utils/router";
import { Box } from "@repo/ui/box";
import { Button } from "@repo/ui/button";
import * as Container from "@repo/ui/container";
import * as Flex from "@repo/ui/flex";
import { Input } from "@repo/ui/input";
import * as List from "@repo/ui/list";
import * as NavigationMenu from "@repo/ui/navigation-menu";
import * as Sheet from "@repo/ui/sheet";
import { useMediaQuery } from "@repo/ui/useMediaQuery";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import {
  setRecentProductSearches,
  getRecentProductSearches,
  resetRecentProductSearches,
} from "@repo/utils/recent-product-searches";
import { useFloorContext } from "@/components/floor-context/floor-context";
import { ProductCarousel } from "@/components/product-carousel";
import {
  CAROUSEL,
  TRENDING_CATEGORY_ID_TOPSHOP,
  TRENDING_CATEGORY_ID_TOPMAN,
} from "@/constants";
import { SearchIcon } from "@/theme/icons/search";

import {
  TYPOGRAPHY_LABEL2_SENTENCE,
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_BODY2_SENTENCE,
  TYPOGRAPHY_LINK2_CAPS,
  TYPOGRAPHY_LABEL3_SENTENCE,
  TYPOGRAPHY_HEADING3_BOLD,
  TYPOGRAPHY_HEADING4,
  TYPOGRAPHY_BODY3,
  TYPOGRAPHY_HEADING1_BOLD,
} from "@/theme/topshop-2/typography/typography-constants";
import { clientRequest } from "@/utils/request-adapters/client-request";
import { CloseIcon } from "../../theme/icons/close";
import { useSmallViewport } from "../viewport";
import * as Viewport from "../viewport";
import classes from "./nav-search.module.css";

type NavSearchProps = {
  store: string;
  lang: string;
  keyStoreDataversion: string;
  onClose?: () => void;
  country: string;
  currency: string;
  shop: string;
  context?: "dropdown" | "drawer";
};

type NavSearchDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  store: string;
  lang: string;
  keyStoreDataversion: string;
  shop: string;
  country: string;
  currency: string;
};

type NavSearchDropdownProps = {
  store: string;
  lang: string;
  keyStoreDataversion: string;
  country: string;
  currency: string;
  shop: string;
};

export const NavSearch = ({
  store,
  lang,
  keyStoreDataversion,
  country,
  onClose,
  shop,
  currency,
  context = "dropdown",
}: NavSearchProps) => {
  const [suggestions, setSuggestions] = React.useState<Suggestion[]>([]);
  const [trendingCategory, setTrendingCategory] =
    useState<BrowseSearchV2 | null>(null);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [recentSearches, setRecentSearches] = React.useState<string[]>([]);

  const isMobile = useSmallViewport();
  const { floor } = useFloorContext();

  const trendingCategoryId =
    floor === "topman"
      ? TRENDING_CATEGORY_ID_TOPMAN
      : TRENDING_CATEGORY_ID_TOPSHOP;

  const router = useRouter();

  const CloseWrapper = ({ children }: { children: React.ReactElement }) => {
    if (context === "drawer") {
      return <Sheet.Close asChild>{children}</Sheet.Close>;
    }
    return (
      <NavigationMenu.CloseDropdown asChild>
        {children}
      </NavigationMenu.CloseDropdown>
    );
  };

  const debouncedGetSuggestions = useMemo(
    () =>
      debounce(async (query: string) => {
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 2) {
          setSuggestions([]);
          return;
        }
        try {
          const suggestionsResponse = await getSuggestions({
            chameleonRequestInstance: clientRequest,
            store,
            lang,
            keyStoreDataversion,
            q: trimmedQuery,
            limit: 10,
          });
          const suggestionsData =
            suggestionsResponse?.data?.data?.suggestionGroups[0]?.suggestions;
          if (!suggestionsData) {
            setSuggestions([]);
            return;
          }
          setSuggestions(suggestionsData);
        } catch (error) {
          console.error("Failed to fetch suggestions:", error);
        }
      }, 300),
    [keyStoreDataversion, lang, store],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      debouncedGetSuggestions(e.target.value);
    },
    [debouncedGetSuggestions],
  );

  const highlightSearchSuggestionsMatch = (text: string, query: string) => {
    const capitalizedText = text.replace(/\b\w/g, (char) => char.toUpperCase());
    const lowerText = capitalizedText.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1) {
      return <>{capitalizedText}</>;
    }

    const beforeMatch = capitalizedText.substring(0, index);
    const match = capitalizedText.substring(index, index + query.length);
    const afterMatch = capitalizedText.substring(index + query.length);

    return (
      <>
        {beforeMatch}
        <strong>{match}</strong>
        {afterMatch}
      </>
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      return;
    }
    handleSearchClear();
    setRecentProductSearches(searchTerm);
    setRecentSearches(getRecentProductSearches());
    handleClose();
    router.push(`/search?q=${encodeURIComponent(searchTerm)}`);
  };

  const handleLinkSubmit = (suggestionsSearchTerm: string) => {
    setRecentProductSearches(suggestionsSearchTerm);
    setRecentSearches(getRecentProductSearches());
    handleSearchClear();

    router.push(`/search?q=${encodeURIComponent(suggestionsSearchTerm)}`);
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setSuggestions([]);
  };

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const getTrendingCategoryProducts = useCallback(async () => {
    try {
      const result = await getCategoryProducts({
        requestInstance: clientRequest,
        categoryId: trendingCategoryId,
        store,
        currency,
        keyStoreDataversion,
        country,
        lang,
        shop,
        limit: 12,
      });
      setTrendingCategory(result ?? null);
    } catch (error) {
      setTrendingCategory(null);
    }
  }, [
    trendingCategoryId,
    store,
    currency,
    keyStoreDataversion,
    country,
    lang,
    shop,
  ]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setRecentSearches(getRecentProductSearches());
    }
    getTrendingCategoryProducts();
  }, [getTrendingCategoryProducts]);

  return (
    <Flex.Container column gap="lg">
      <Flex.Container column>
        <form onSubmit={handleSubmit}>
          <Flex.Container gap="md" justify="space-between" align="center">
            <Input
              className={clsx(classes.searchInput, TYPOGRAPHY_LABEL2_SENTENCE)}
              type="search"
              placeholder="Enter Keywords"
              onChange={handleChange}
              value={searchTerm}
            />
            <Flex.Container gap="md" align="center">
              {searchTerm.length > 0 && (
                <Button
                  onClick={handleSearchClear}
                  className={TYPOGRAPHY_LABEL3}
                  type="button"
                >
                  CLEAR
                </Button>
              )}
              <NavigationMenu.CloseDropdown asChild>
                <Button size="icon" type="submit">
                  <SearchIcon
                    className={clsx(classes.icon, classes.blendText)}
                    label="Search"
                  />
                </Button>
              </NavigationMenu.CloseDropdown>
            </Flex.Container>
          </Flex.Container>
        </form>
        <hr className={classes.searchInputHorizontalRule}></hr>
      </Flex.Container>
      <Flex.Container justify="space-between" align="center">
        {suggestions.length ? (
          <Flex.Container gap="3xs" column>
            {suggestions.map((suggestion) => (
              <Box paddingY="2xs" asChild>
                <CloseWrapper key={suggestion.searchTerm}>
                  <Button
                    type="button"
                    onClick={() => {
                      handleLinkSubmit(suggestion.searchTerm);
                    }}
                    alignment="start"
                    className={clsx(
                      classes.searchSuggestionLink,
                      TYPOGRAPHY_LABEL3_SENTENCE,
                    )}
                  >
                    {highlightSearchSuggestionsMatch(
                      suggestion.searchTerm,
                      searchTerm,
                    )}
                  </Button>
                </CloseWrapper>
              </Box>
            ))}
          </Flex.Container>
        ) : (
          <Flex.Container
            column
            gap="sm"
            className={classes.recentSearchesContainer}
          >
            <Viewport.Small>
              <Flex.Container justify="space-between" align="center">
                <h3 className={TYPOGRAPHY_HEADING3_BOLD}>Recent Searches</h3>
                {recentSearches.length ? (
                  <Button
                    alignment="start"
                    className={TYPOGRAPHY_LINK2_CAPS}
                    onClick={() => {
                      resetRecentProductSearches();
                      setRecentSearches([]);
                    }}
                  >
                    Clear all
                  </Button>
                ) : null}
              </Flex.Container>
            </Viewport.Small>
            <Viewport.Large>
              <h3 className={TYPOGRAPHY_HEADING3_BOLD}>Recent Searches</h3>
            </Viewport.Large>
            {recentSearches.length ? (
              <Flex.Container gap="md" column>
                <Flex.Container column gap="xs">
                  {recentSearches.map((recentSearch) => (
                    <CloseWrapper key={recentSearch}>
                      <Button
                        className={TYPOGRAPHY_LABEL3_SENTENCE}
                        alignment="start"
                        onClick={() => {
                          handleLinkSubmit(recentSearch);
                        }}
                      >
                        {recentSearch}
                      </Button>
                    </CloseWrapper>
                  ))}
                </Flex.Container>

                <Viewport.Large>
                  <Button
                    alignment="start"
                    className={TYPOGRAPHY_LINK2_CAPS}
                    onClick={() => {
                      resetRecentProductSearches();
                      setRecentSearches([]);
                    }}
                  >
                    Clear all
                  </Button>
                </Viewport.Large>
              </Flex.Container>
            ) : (
              <p className={TYPOGRAPHY_BODY2_SENTENCE}>No recent searches</p>
            )}
          </Flex.Container>
        )}
      </Flex.Container>

      {trendingCategory && trendingCategory.products.length >= 4 && (
        <Flex.Container column className={classes.trendingContainer} gap="lg">
          <Flex.Container justify="space-between" align="center">
            <h1 className={TYPOGRAPHY_HEADING1_BOLD}>Trending</h1>
            <CloseWrapper>
              <Link
                href={`/${floor}/category/trending?cid=${floor === "topshop" ? TRENDING_CATEGORY_ID_TOPSHOP : TRENDING_CATEGORY_ID_TOPMAN}`}
                className={TYPOGRAPHY_LINK2_CAPS}
              >
                See more
              </Link>
            </CloseWrapper>
          </Flex.Container>

          <div className={classes.carouselContainer}>
            <Container.Break asChild className={classes.containerBreak}>
              <List.Root snap>
                <ProductCarousel
                  products={trendingCategory?.products}
                  store={store}
                  country={country}
                  currency={currency}
                  sourceSetProfile={CAROUSEL}
                  listItemStyle={{
                    width: isMobile ? "max(240px)" : "max(312px)",
                  }}
                  productTitleClassName={TYPOGRAPHY_HEADING4}
                  productPriceClassName={TYPOGRAPHY_BODY3}
                  showProductInfo={isMobile ? true : false}
                  overlayProductInfo={isMobile ? false : true}
                  wrapper={CloseWrapper}
                />
              </List.Root>
            </Container.Break>
          </div>
        </Flex.Container>
      )}
    </Flex.Container>
  );
};

export const NavSearchDrawer = ({
  store,
  lang,
  keyStoreDataversion,
  isOpen,
  onClose,
  country,
  currency,
  shop,
}: NavSearchDrawerProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );

  return (
    <>
      <Sheet.Root open={isOpen} onOpenChange={onClose}>
        <Sheet.Content
          position="left"
          overlayAnimation={!prefersReducedMotion}
          className={classes.drawerContainer}
        >
          <Flex.Container column className={classes.content} gap="md">
            <Sheet.Header>
              <Flex.Container
                className={classes.header}
                justify="start"
                align="center"
              >
                <Sheet.Title>
                  <VisuallyHidden>Search Menu</VisuallyHidden>
                </Sheet.Title>
                <Sheet.Description>
                  <VisuallyHidden>
                    Navigation menu for searching products
                  </VisuallyHidden>
                </Sheet.Description>
                <Sheet.Close ref={closeButtonRef} asChild>
                  <Button size="icon" type="button">
                    <CloseIcon
                      label="Close menu"
                      className={clsx(classes.closeIcon, classes.blendText)}
                    />
                  </Button>
                </Sheet.Close>
              </Flex.Container>
            </Sheet.Header>
            <Sheet.Main className={classes.main}>
              <NavSearch
                store={store}
                lang={lang}
                keyStoreDataversion={keyStoreDataversion}
                onClose={onClose}
                country={country}
                currency={currency}
                shop={shop}
                context="drawer"
              />
            </Sheet.Main>
          </Flex.Container>
        </Sheet.Content>
      </Sheet.Root>
    </>
  );
};

export const NavSearchDropdown = ({
  store,
  lang,
  keyStoreDataversion,
  country,
  currency,
  shop,
}: NavSearchDropdownProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <NavigationMenu.Item>
      <NavigationMenu.DropdownItemToggle
        ref={triggerRef}
        className={classes.trigger}
        asChild
      >
        <Button size="icon">
          <SearchIcon
            label="search"
            className={clsx(classes.icon, classes.blendText)}
          />
        </Button>
      </NavigationMenu.DropdownItemToggle>

      <NavigationMenu.DropdownItemContent className={classes.dropdownContainer}>
        <div className={classes.dropdownOverlay} />
        <Container.Root size="medium" gutter="3xl">
          <NavSearch
            store={store}
            lang={lang}
            keyStoreDataversion={keyStoreDataversion}
            country={country}
            currency={currency}
            shop={shop}
            context="dropdown"
          />
        </Container.Root>
      </NavigationMenu.DropdownItemContent>
    </NavigationMenu.Item>
  );
};
